import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import {
  proactiviteeLiveQuery,
  proactiviteeSummaryQuery,
  proactiviteeWatch,
  type DeviceProactiviteeSummaryPage,
  type ProactiviteeLivePayload,
  type ProactiviteePerfTile,
} from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Proactivitee Summary - proactivitee/devices/details/deviceSummary.php:
 * aplicatia de baza, alarmele deschise, Live performance (inele + praguri),
 * Operating system, Security, iar pe server Service operation si Primary
 * service din device_primary_service.php.
 */

const BOTTLENECK: Record<string, string> = {
  cpu: 'CPU',
  ram: 'RAM',
  disk: 'Disk',
  gpu: 'GPU',
  none: 'Balanced',
}

function AnimatedNumber({ value }: { value: number }) {
  const [shown, setShown] = useState(0)
  useEffect(() => {
    let frame = 0
    const start = performance.now()
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / 400)
      const swing = 0.5 - Math.cos(t * Math.PI) / 2
      setShown(Math.round(value * swing))
      if (t < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [value])
  return (
    <div className="number number-summary" data-number={value}>
      {shown}
    </div>
  )
}

function Mark({ ok }: { ok: boolean }) {
  return ok ? (
    <i className="fas fa-check-circle font-20 color-green2"></i>
  ) : (
    <i className="fas fa-minus-circle font-20 color-red"></i>
  )
}

function tileTone(
  metric: string | null,
  value: number | null,
  thresholds: Record<string, number>,
  pairs: { metric: string; warn: string; crit: string }[],
): string {
  if (metric === null || value === null || Number.isNaN(value)) return 'blue-madison'
  const pair = pairs.find((p) => p.metric === metric)
  if (!pair) return 'blue-madison'
  if (value >= thresholds[pair.crit]!) return 'red-intense'
  if (value >= thresholds[pair.warn]!) return 'yellow'
  return 'blue-madison'
}

function shortDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 1) return '0s'
  const units: [string, number][] = [
    ['z', 86400],
    ['h', 3600],
    ['m', 60],
    ['s', 1],
  ]
  for (let i = 0; i < units.length; i++) {
    const [label, size] = units[i]!
    const value = Math.floor(seconds / size)
    if (value <= 0) continue
    let out = `${value}${label}`
    const next = units[i + 1]
    if (next) {
      const rest = Math.floor((seconds - value * size) / next[1])
      if (rest > 0) out += ` ${rest}${next[0]}`
    }
    return out
  }
  return '0s'
}

function applyLive(
  tiles: ProactiviteePerfTile[],
  live: ProactiviteeLivePayload,
  thresholds: Record<string, number>,
  pairs: { metric: string; warn: string; crit: string }[],
): ProactiviteePerfTile[] {
  if (!live.has) return tiles
  const score = Math.round(live.score || 0)
  const scoreTone = score < 50 ? 'red-intense' : score < 80 ? 'yellow' : 'green-haze'
  const bn = BOTTLENECK[(live.bottleneck || '').toLowerCase()] || 'n/a'
  const cpuUtil = Number(live.cpu_util || 0)
  const ramUsed = Number(live.ram_used_percent || 0)
  const diskUtil = Number(live.disk_util || 0)
  const ioWait = Number(live.cpu_iowait || 0)
  const swapPct = Number(live.ram_swap_percent || 0)
  const diskAwait = Number(live.disk_await_ms || 0)
  const load1 = Number(live.cpu_load1 || 0)
  const cpuCount = Number(live.cpu_count || 0)
  const perCore = cpuCount > 0 ? load1 / cpuCount : 0
  const netTotal = live.net_connections || 0
  const netIn = live.net_in || 0
  const netOut = live.net_out || 0
  const apps = live.running_applications
  const gpu =
    live.gpu_util !== null && live.gpu_util !== '' && live.gpu_util !== undefined
      ? `${Number(live.gpu_util).toFixed(1)}%`
      : null
  const ring = (percent: number) => {
    const length = 2 * Math.PI * 26
    return { ring_length: Math.round(length * 100) / 100, ring_offset: Math.round(length * (1 - Math.max(0, Math.min(100, percent)) / 100) * 100) / 100 }
  }
  return tiles.map((tile) => {
    switch (tile.id) {
      case 'live-perf-headroom':
        return { ...tile, tone: scoreTone, number: String(score), more: `Bottleneck: ${bn}`, ...ring(score) }
      case 'live-perf-cpu-util':
        return {
          ...tile,
          tone: tileTone('load_per_core', perCore, thresholds, pairs),
          number: `${cpuUtil.toFixed(1)}%`,
          more: `Load ${load1.toFixed(2)} / ${cpuCount}`,
          metric_value: perCore,
          ...ring(cpuUtil),
        }
      case 'live-perf-ram-used':
        return {
          ...tile,
          tone: tileTone('ram_used', ramUsed, thresholds, pairs),
          number: `${ramUsed.toFixed(1)}%`,
          more: `${live.ram_available || ''} available`,
          metric_value: ramUsed,
          ...ring(ramUsed),
        }
      case 'live-perf-disk-util':
        return {
          ...tile,
          tone: tileTone('disk_await_ms', diskAwait, thresholds, pairs),
          number: `${diskUtil.toFixed(1)}%`,
          more: `${diskAwait.toFixed(1)} ms await`,
          metric_value: diskAwait,
          ...ring(diskUtil),
        }
      case 'live-perf-net-total':
        return { ...tile, number: String(netTotal), more: `${netIn} in · ${netOut} out` }
      case 'live-perf-apps':
        return apps ? { ...tile, number: String(apps) } : tile
      case 'live-perf-uptime':
        return typeof live.uptime_seconds === 'number' ? { ...tile, number: shortDuration(live.uptime_seconds) } : tile
      case 'live-perf-cpu-iowait':
        return { ...tile, number: `${ioWait.toFixed(1)}%`, more: `Steal ${Number(live.cpu_steal || 0).toFixed(1)}%`, ...ring(ioWait) }
      case 'live-perf-ram-swap':
        return {
          ...tile,
          tone: tileTone('swap', swapPct, thresholds, pairs),
          number: `${swapPct.toFixed(1)}%`,
          more: `${live.ram_swap_used || 'none'} / ${live.ram_swap_total || 'none'}`,
          metric_value: swapPct,
          ...ring(swapPct),
        }
      case 'live-perf-disk-read':
        return {
          ...tile,
          number: live.disk_read_bps || tile.number,
          more: `Write ${live.disk_write_bps || ''}`,
        }
      case 'live-perf-net-established':
        return { ...tile, number: String(live.net_established || 0), more: `Of ${netTotal} connections` }
      case 'live-perf-gpu':
        return gpu ? { ...tile, number: gpu } : tile
      default:
        return tile
    }
  })
}

function Ring({ tile }: { tile: ProactiviteePerfTile }) {
  if (tile.ring === null) {
    return <i className={`fas ${tile.icon || 'fa-circle'}`}></i>
  }
  return (
    <svg className="pm-ring" viewBox="0 0 64 64" data-ring={tile.ring}>
      <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,.25)" strokeWidth="7" />
      <circle
        className="pm-ring-fg"
        cx="32"
        cy="32"
        r="26"
        fill="none"
        stroke="#fff"
        strokeWidth="7"
        strokeLinecap="round"
        transform="rotate(-90 32 32)"
        strokeDasharray={String(tile.ring_length ?? '')}
        strokeDashoffset={String(tile.ring_offset ?? '')}
      />
    </svg>
  )
}

function PerfTile({ tile }: { tile: ProactiviteePerfTile }) {
  return (
    <div className="col-md-2">
      <div
        className={`dashboard-stat ${tile.tone}`}
        {...(tile.metric
          ? { 'data-tile-metric': tile.metric, 'data-tile-value': String(tile.metric_value ?? '') }
          : {})}
      >
        <div className="visual">
          <Ring tile={tile} />
        </div>
        <div className="details">
          <div className={`number${tile.small ? ' live-perf-small' : ''}`} id={tile.id}>
            {tile.number}
          </div>
          <div className="desc">{tile.label}</div>
        </div>
        <div className="more" dangerouslySetInnerHTML={{ __html: tile.more }} />
      </div>
    </div>
  )
}

function LivePerformance({
  page,
  live,
  network,
  running,
}: {
  page: DeviceProactiviteeSummaryPage
  live: ProactiviteeLivePayload | undefined
  network: string
  running: string
}) {
  const tiles = useMemo(() => {
    const base = page.performance?.tiles ?? []
    if (!live) return base
    return applyLive(base, live, page.thresholds, page.saturation_pairs)
  }, [live, page.performance, page.saturation_pairs, page.thresholds])
  if (!page.performance) return null
  return (
    <div className="row" id="live-performance-panel" data-device={page.device.id} data-network={network} data-running={running}>
      <div className="col-md-12 divider">
        <h4 className="bordered">Live performance</h4>
      </div>
      {tiles.map((tile) => (
        <PerfTile key={tile.id} tile={tile} />
      ))}
    </div>
  )
}

function OperatingSystem({ page, network, running }: { page: DeviceProactiviteeSummaryPage; network: string; running: string }) {
  return (
    <div className="row">
      <div className="col-md-12 divider">
        <h4 className="bordered">Operating system</h4>
      </div>
      {!page.os && (
        <div className="col-md-12">
          <div className="alert alert-warning">No available informations.</div>
        </div>
      )}
      {page.os && (
        <>
          <div className="col-md-6">
            <table className="mlist-details" width="100%">
              <tbody>
                <tr>
                  <td width="150">
                    <b>Install date: </b>
                  </td>
                  <td>{page.os.install_date}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Last boot: </b>
                  </td>
                  <td>{page.os.last_boot}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Uptime: </b>
                  </td>
                  <td>{page.os.uptime}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Changes (last 24h): </b>
                  </td>
                  <td>{page.os.changes_24h}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>OS updates: </b>
                  </td>
                  <td>{page.os.os_updates}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Active processes: </b>
                  </td>
                  <td>{page.os.processes}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Running applications: </b>
                  </td>
                  <td id="live-running-applications">{running}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Reboot pending: </b>
                  </td>
                  <td>{page.os.reboot_pending}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="col-md-6">
            <table className="mlist-details" width="100%">
              <tbody>
                <tr>
                  <td width="150">
                    <b>Total RAM: </b>
                  </td>
                  <td>{page.os.total_ram}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Free RAM: </b>
                  </td>
                  <td>{page.os.free_ram}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>RAM usage: </b>
                  </td>
                  <td>{page.os.ram_usage}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Total SWAP: </b>
                  </td>
                  <td>{page.os.total_swap}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Free SWAP: </b>
                  </td>
                  <td>{page.os.free_swap}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>SWAP usage: </b>
                  </td>
                  <td>{page.os.swap_usage}</td>
                </tr>
                <tr>
                  <td width="150">
                    <b>Network connections: </b>
                  </td>
                  <td id="live-os-net">{network}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function Security({ page }: { page: DeviceProactiviteeSummaryPage }) {
  return (
    <div className="row">
      <div className="col-md-12 divider">
        <h4 className="bordered">Security</h4>
      </div>
      <div className="col-md-12 ">
        <table className="table table-striped centered">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Enabled</th>
              <th>Up to date</th>
            </tr>
          </thead>
          <tbody>
            {page.security.length === 0 && (
              <tr>
                <td colSpan={4}>No security informations...</td>
              </tr>
            )}
            {page.security.map((soft) => (
              <tr key={`${soft.name}-${soft.category}`}>
                <td>{soft.name}</td>
                <td>{soft.category}</td>
                <td>
                  <Mark ok={soft.enabled} />
                </td>
                <td>
                  <Mark ok={soft.up_to_date} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ServiceOperation({ page }: { page: DeviceProactiviteeSummaryPage }) {
  const s = page.service
  if (!s) return null
  return (
    <div className="row row-content">
      <div className="col-md-12 divider">
        <h4 className="bordered">
          Service operation
          {s.role_label !== '' && (
            <small>
              {' '}
              {s.role_label} · {s.status}
              {s.others.map((o) => (
                <span key={o}> · {o}</span>
              ))}
            </small>
          )}
        </h4>
      </div>
      {s.next_text !== '' && (
        <div className="col-md-12 margin-bottom-10">
          <div className={`alert alert-${s.next_cls}`} style={{ marginBottom: 10 }}>
            <strong>Next:</strong> {s.next_text}
            {s.reasons.length > 0 && (
              <>
                <br />
                <small>{s.reasons.join(' · ')}</small>
              </>
            )}
          </div>
        </div>
      )}
      {s.kpi && (
        <div className="col-md-3">
          <div className={`dashboard-stat ${s.kpi.tone}`}>
            <div className="visual">
              <i className="fas fa-tachometer-alt"></i>
            </div>
            <div className="details">
              <div className="number" style={{ fontSize: 22, lineHeight: '31px' }}>
                {s.kpi.display}
              </div>
              <div className="desc">{s.kpi.label}</div>
            </div>
            <div className="more">Most relevant</div>
          </div>
        </div>
      )}
      <div className={`col-md-${s.kpi ? '9' : '12'}`}>
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Signal</th>
              <th width={140}>Value</th>
            </tr>
          </thead>
          <tbody>
            {s.signals.length === 0 && (
              <tr>
                <td colSpan={2}>No live service signals yet.</td>
              </tr>
            )}
            {s.signals.map((sig) => (
              <tr key={sig.label}>
                <td>{sig.label}</td>
                <td>{sig.display}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PrimaryService({ page }: { page: DeviceProactiviteeSummaryPage }) {
  const p = page.primary
  if (!p) return null
  return (
    <>
      <div className="row" style={{ marginTop: 10 }}>
        <div className="col-md-12 divider">
          <h4 className="bordered">Primary service</h4>
        </div>
        {p.next_text !== '' && (
          <div className="col-md-12 margin-bottom-10">
            <div className={`alert alert-${p.next_cls}`} style={{ marginBottom: 10 }}>
              <strong>Next:</strong> {p.next_text}
              {p.reasons.length > 0 && (
                <>
                  <br />
                  <small>{p.reasons.join(' · ')}</small>
                </>
              )}
            </div>
          </div>
        )}
        <div className="col-md-3">
          <div className="dashboard-stat blue-madison">
            <div className="visual">
              <i className="fas fa-server"></i>
            </div>
            <div className="details">
              <div
                className="number"
                style={{ fontSize: 18, lineHeight: '31px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={p.name}
              >
                {p.name}
              </div>
              <div className="desc">{p.role_label}</div>
            </div>
            <div className="more">{p.role_matched}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className={`dashboard-stat ${p.status_tone}`}>
            <div className="visual">
              <i className="fas fa-heartbeat"></i>
            </div>
            <div className="details">
              <div className="number" style={{ fontSize: 18, lineHeight: '31px', textTransform: 'capitalize' }}>
                {p.status}
              </div>
              <div className="desc">Status</div>
            </div>
            <div className="more">{p.health_summary}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className={`dashboard-stat ${p.version_tone}`}>
            <div className="visual">
              <i className="fas fa-code-branch"></i>
            </div>
            <div className="details">
              <div className="number" style={{ fontSize: 16, lineHeight: '31px' }} title={p.version_title}>
                {p.version}
              </div>
              <div className="desc">{p.version_desc}</div>
            </div>
            <div className="more">{p.package}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className={`dashboard-stat ${p.updates_tone}`}>
            <div className="visual">
              <i className="fas fa-shield-alt"></i>
            </div>
            <div className="details">
              <AnimatedNumber value={p.updates} />
              <div className="desc">{p.updates_label}</div>
            </div>
            <div className="more" title={p.updates_more}>
              {p.updates_more}
            </div>
          </div>
        </div>
      </div>
      {p.config_items.length > 0 && (
        <div className="row row-content">
          <div className="col-md-12 divider">
            <h4 className="bordered">
              Configuration review
              {p.config_drift > 0 && <small className="text-warning"> {p.config_drift} recent change(s)</small>}
            </h4>
          </div>
          <div className="col-md-12">
            <table className="table table-striped table-hover psvc-config" width="100%">
              <thead>
                <tr>
                  <th>Path</th>
                  <th width={120}>Status</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {p.config_items.map((item) => (
                  <tr key={item.path}>
                    <td>
                      <code>{item.path}</code>
                    </td>
                    <td>
                      <span className={`label label-${item.badge}`}>{item.status}</span>
                    </td>
                    <td>{item.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}

export function DeviceProactiviteeSummary({ id }: ScreenProps) {
  const { data, error } = useQuery(proactiviteeSummaryQuery(id))
  const live = useQuery({
    ...proactiviteeLiveQuery(id),
    enabled: !!data?.performance,
  })

  useEffect(() => {
    if (!data?.performance) return
    void proactiviteeWatch(id)
    const timer = window.setInterval(() => {
      void proactiviteeWatch(id)
    }, 3000)
    return () => window.clearInterval(timer)
  }, [data?.performance, id])

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const latest = live.data && live.data.has ? live.data : (data.live.has ? (data.live as ProactiviteeLivePayload) : undefined)
  const network =
    latest && latest.has
      ? `${latest.net_connections || 0} total (${latest.net_in || 0} in / ${latest.net_out || 0} out)`
      : data.os?.network || 'n/a'
  const running =
    latest && latest.running_applications
      ? String(latest.running_applications)
      : data.os?.running_applications || 'n/a'

  return (
    <>
      <div className="row">
        <div className="col-md-2">
          <div className="dashboard-stat blue-madison core-app-stat">
            <div className="visual">
              <i className="fa fa-window-maximize"></i>
            </div>
            <div className="details">
              <div className="number core-app-name" title={data.core.name}>
                {data.core.name !== '' ? data.core.name : <span dangerouslySetInnerHTML={{ __html: '&ndash;' }} />}
              </div>
              <div className="desc" title={data.core.focus}>
                {data.core.focus !== '' ? data.core.focus : <span dangerouslySetInnerHTML={{ __html: '&ndash;' }} />}
              </div>
            </div>
            <div className="more">Core Application</div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="dashboard-stat red-intense">
            <div className="visual">
              <i className="fa fa-bell"></i>
            </div>
            <div className="details">
              <AnimatedNumber value={data.alarms.critical} />
              <div className="desc">Critical</div>
            </div>
            <div className="more">Active alarms</div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="dashboard-stat yellow">
            <div className="visual">
              <i className="fa fa-bell"></i>
            </div>
            <div className="details">
              <AnimatedNumber value={data.alarms.major} />
              <div className="desc">Major</div>
            </div>
            <div className="more">Active alarms</div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="dashboard-stat green-haze">
            <div className="visual">
              <i className="fa fa-bell"></i>
            </div>
            <div className="details">
              <AnimatedNumber value={data.alarms.minor} />
              <div className="desc">Minor</div>
            </div>
            <div className="more">Active alarms</div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="dashboard-stat blue-madison">
            <div className="visual">
              <i className="fa fa-bell"></i>
            </div>
            <div className="details">
              <AnimatedNumber value={data.alarms.info} />
              <div className="desc">Info</div>
            </div>
            <div className="more">Active alarms</div>
          </div>
        </div>
        <div className="col-md-2">
          <div className="dashboard-stat grey">
            <div className="visual">
              <i className="fa fa-bell"></i>
            </div>
            <div className="details">
              <AnimatedNumber value={data.alarms.closed} />
              <div className="desc">Closed</div>
            </div>
            <div className="more">Alarms</div>
          </div>
        </div>
      </div>
      <LivePerformance page={data} live={latest} network={network} running={running} />
      <OperatingSystem page={data} network={network} running={running} />
      <Security page={data} />
      <ServiceOperation page={data} />
      <PrimaryService page={data} />
    </>
  )
}
